// client/src/components/clientes/FiltroClientes.js
import React, { useState, useCallback } from 'react';
import { Search, X } from 'lucide-react';

const initialFilterState = {
    q: '',
    cpf: '',
    status: '',
    sort: '-createdAt',
    dataInicio: '',
    dataFim: '',
};

const FiltroClientes = ({ onApplyFilters, loading = false }) => {
    const [localFilters, setLocalFilters] = useState(initialFilterState);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setLocalFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        const filters = {};
        if (localFilters.q.trim()) filters.q = localFilters.q.trim();
        if (localFilters.cpf.trim()) filters.cpf = localFilters.cpf.trim().replace(/\D/g, '');
        if (localFilters.status) filters.status = localFilters.status;
        if (localFilters.dataInicio) filters.dataInicio = localFilters.dataInicio;
        if (localFilters.dataFim) filters.dataFim = localFilters.dataFim;
        filters.sort = localFilters.sort;
        onApplyFilters(filters);
    }, [localFilters, onApplyFilters]);

    const handleClear = useCallback(() => {
        setLocalFilters(initialFilterState);
        onApplyFilters({});
    }, [onApplyFilters]);

    const hasFilters =
        localFilters.q !== '' ||
        localFilters.cpf !== '' ||
        localFilters.status !== '' ||
        localFilters.sort !== '-createdAt' ||
        localFilters.dataInicio !== '' ||
        localFilters.dataFim !== '';

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Linha 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600 uppercase">Buscar</label>
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            name="q"
                            value={localFilters.q}
                            onChange={handleChange}
                            placeholder="Nome, email ou telefone..."
                            className="form-input text-sm py-2 pl-10 pr-3 rounded border border-gray-300 outline-none w-full"
                            disabled={loading}
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600 uppercase">CPF</label>
                    <input
                        type="text"
                        name="cpf"
                        value={localFilters.cpf}
                        onChange={handleChange}
                        placeholder="000.000.000-00"
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 outline-none w-full"
                        disabled={loading}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600 uppercase">Status</label>
                    <select
                        name="status"
                        value={localFilters.status}
                        onChange={handleChange}
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 outline-none bg-white w-full"
                        disabled={loading}
                    >
                        <option value="">Todos os Status</option>
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600 uppercase">Ordenar por</label>
                    <select
                        name="sort"
                        value={localFilters.sort}
                        onChange={handleChange}
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 outline-none bg-white w-full"
                        disabled={loading}
                    >
                        <option value="-createdAt">Mais Recentes</option>
                        <option value="createdAt">Mais Antigos</option>
                        <option value="nome">Nome A-Z</option>
                        <option value="-nome">Nome Z-A</option>
                    </select>
                </div>
            </div>

            {/* Linha 2 — datas + botões */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600 uppercase">Cadastrado a partir de</label>
                    <input
                        type="date"
                        name="dataInicio"
                        value={localFilters.dataInicio}
                        onChange={handleChange}
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 outline-none w-full"
                        disabled={loading}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600 uppercase">Até</label>
                    <input
                        type="date"
                        name="dataFim"
                        value={localFilters.dataFim}
                        onChange={handleChange}
                        className="form-input text-sm py-2 px-3 rounded border border-gray-300 outline-none w-full"
                        disabled={loading}
                    />
                </div>

                <div /> {/* espaço vazio */}

                <div className="flex gap-2 h-full">
                    {/* Antes essa cor/estilo era reinventado do zero em Tailwind
                        (bg-blue-600, borda vermelha custom) em vez de usar as
                        classes action-button padrão que o resto do app usa
                        (inclusive o FiltroAgendamentos.js, mesmo papel) — e o
                        texto "Filtrar" só aparecia acima de 1280px (hidden
                        xl:inline), sumindo em boa parte das telas de laptop. */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="action-button action-button-primary flex-1 justify-center"
                    >
                        <Search size={18} className="icon" /> Aplicar Filtros
                    </button>
                    {hasFilters && (
                        <button
                            type="button"
                            onClick={handleClear}
                            disabled={loading}
                            className="action-button action-button-secondary justify-center"
                            title="Limpar Filtros"
                        >
                            <X size={18} className="icon" />
                        </button>
                    )}
                </div>
            </div>
        </form>
    );
};

export default FiltroClientes;
